// src/App.js
// ============================================================
//  ANNIHILATION — the router. (v5 — Batch 6)
//  One `screen` string routes everything. No router library.
//  (IE law — PRIME guide, 4xS "Simple".)
//
//  Screens live in this file:
//    "menu"   → BootSequence (title cinematic → plaza)
//    "plaza"  → Plaza (the walkable Old Fold — internal id stays
//               "plaza" per the Vyren pattern; display strings
//               get the village rename, code ids never do)
//    "trainer" → LIVE (v3): Transcendence gate + SkillTreeView.
//    "yard"   → LIVE (v4): Sigfer's Yard — the proving ground.
//    "bonfire" → LIVE (v3): the Rest.
//    "gate"   → LIVE (v5): the departure point. The Whispering
//               Grove trail is open; the Glass Flats trail is
//               marked but uncut (Batch 16).
//    "grove"  → LIVE (v5): Outskirts North — the Whispering
//               Grove. The first real field (Batch 6).
//    "sunken_fold" → LIVE (v5): the first dungeon. The Descent,
//               the Crypt, HER.
//    other building screens (marketplace / forge / registrar /
//               bulletin / garden) → placeholder rooms driven by
//               villageData until each ships real
//    "wilds"  → placeholder for anything beyond what's built
//
//  DEATH LAW (Systems Spec Part 4): respawn at the village with
//  everything intact; no XP loss; rest-gated things do NOT
//  refresh (dying is not a free rest — nothing here calls the
//  Rest handler). Zones signal it via onDown → respawnAtVillage.
//
//  IE-universal pieces kept intact: goTo/goBack, inbound ?uid=
//  handoff (skip boot), outbound ?uid= handoff to the Pier.
// ============================================================

import React, { useState, useEffect, useCallback } from "react";
import BootSequence from "./BootSequence";
import Plaza from "./Plaza";
import { VILLAGE, getBuilding, getBuildingForScreen } from "./villageData";
import { useAnnihilationProfile } from "./useAnnihilationProfile";
import CharacterCreate from "./CharacterCreate";
import TrainingYard from "./TrainingYard";
import SkillTreeView from "./SkillTreeView";
import SkillBar from "./SkillBar";
import WhisperingGrove, { MATERIALS } from "./whisperingGrove";
import SunkenFold from "./sunkenFold";
import { saveCharacter, deltaBloodMoney, deltaMaterial } from "./profileWrites";
import { onVillageRest } from "./treeEngine";
import { normalizeTreeState } from "./trees";
import { levelForXp } from "./stats";

const PIER_URL = "https://unstable-pierre.pages.dev";

// Anything a village portal points at that isn't a wired room yet
// falls through to the wilds placeholder — never a blank screen.
const KNOWN_ROOMS = [
  "bonfire",
  "yard",
  "marketplace",
  "forge",
  "trainer",
  "registrar",
  "bulletin",
  "gate",
  "garden",
  "grove",
  "sunken_fold",
  "wilds",
];

const WILDS_STUB = {
  name: "The Outskirts",
  keeper: null,
  role:
    "The first ring. The tutorial wilds — the Whispering Grove to the " +
    "north, the Glass Flats to the east.",
  flavor: "Beyond the Gate, the world begins. Not yet — but soon.",
};

export default function App() {
  const [screen, setScreen] = useState("menu");
  const [profile, setProfile] = useState(null);
  const [trail, setTrail] = useState([]); // goBack breadcrumb stack
  const [rested, setRested] = useState(false); // Bonfire feedback flash

  // Live annihilation lane — Blood Crests + character. Idles on
  // null uid; comes alive the moment the Plaza hands a profile up.
  // (Internal field/id stays `bloodMoney` per the Dial Law; only
  // the on-screen name is "Blood Crests" — caP'n, Jul 21 2026.)
  const anni = useAnnihilationProfile(profile?.uid || null);

  // ---------------------------------------------------------
  // IE-universal: inbound ?uid= handoff (PRIME Phase G).
  // Strip the param immediately so a refresh doesn't re-trigger,
  // and skip the boot cinematic — the player already booted
  // somewhere else in the Empire.
  // ---------------------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const incomingUid = params.get("uid");
    if (incomingUid) {
      try {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (e) {
        /* non-blocking */
      }
      setScreen("plaza");
    }
  }, []);

  // ---------------------------------------------------------
  // Navigation helpers — IE-universal, keep intact.
  // ---------------------------------------------------------
  const goTo = useCallback(
    (next) => {
      setTrail((t) => [...t, screen]);
      setScreen(next);
      setRested(false);
    },
    [screen]
  );

  const goBack = useCallback(() => {
    setTrail((t) => {
      if (t.length === 0) {
        setScreen("plaza");
        return t;
      }
      const copy = [...t];
      const prev = copy.pop();
      setScreen(prev);
      return copy;
    });
    setRested(false);
  }, []);

  // Death law: the fall carries you home. Trail clears so a
  // goBack from the square doesn't walk you back into the grave.
  const respawnAtVillage = useCallback(() => {
    setTrail([]);
    setScreen("plaza");
    setRested(false);
  }, []);

  // ---------------------------------------------------------
  // Outbound handoff → Unstable Pierre (the Pier routes the Empire).
  // ---------------------------------------------------------
  const handlePierre = useCallback(
    (prof) => {
      const outUid = (prof && prof.uid) || (profile && profile.uid);
      if (!outUid) {
        console.warn("[Annihilation] Pier handoff aborted — no uid available.");
        return;
      }
      window.location.href = `${PIER_URL}?uid=${encodeURIComponent(outUid)}`;
    },
    [profile]
  );

  // ---------------------------------------------------------
  // Plaza → App contract (PRIME Phase F.2).
  // Portals carry their destination; unknown destinations fall
  // through to the wilds placeholder.
  // ---------------------------------------------------------
  const handleEnterDungeon = useCallback(
    (portal, prof) => {
      if (prof) setProfile(prof);
      const dest =
        (portal && (portal.dest || portal.screen || portal.id)) || "wilds";
      goTo(KNOWN_ROOMS.includes(dest) ? dest : "wilds");
    },
    [goTo]
  );

  const handleEnterShop = useCallback(
    (prof) => {
      if (prof) setProfile(prof);
      goTo("marketplace");
    },
    [goTo]
  );

  const handleForge = useCallback(
    (prof) => {
      if (prof) setProfile(prof);
      goTo("forge");
    },
    [goTo]
  );

  // ONE dispatch for all eight buildings (Plaza's contract).
  // Building id → screen via villageData; anything unknown falls
  // through to the wilds placeholder, never a blank page.
  const handleEnterBuilding = useCallback(
    (buildingId, prof) => {
      if (prof) setProfile(prof);
      const b = getBuilding(buildingId);
      const dest = b?.screen || buildingId;
      goTo(KNOWN_ROOMS.includes(dest) ? dest : "wilds");
    },
    [goTo]
  );

  const handleEnterNPC = useCallback((...args) => {
    // Annihilation has no NPC matches — village NPCs speak through
    // DialogueEngine inside the Plaza itself. Intentional no-op.
    console.log("[Annihilation] onEnterNPC (no-op at hub milestone)", args);
  }, []);

  // ---------------------------------------------------------
  // THE REST — the Bonfire's one verb (rest economy, combat.js
  // REST_SCOPE). Resets Sigfer's refund allowance; when field
  // combat ships, the same handler restores rest-gated ability
  // uses and full Anima/Chao. One handler, one write.
  // ---------------------------------------------------------
  const handleRest = useCallback(async () => {
    const uid = profile?.uid;
    if (!uid || !anni.character) return;
    const next = onVillageRest(normalizeTreeState(anni.character.skillTrees));
    try {
      await saveCharacter(uid, { skillTrees: next });
      setRested(true);
    } catch (e) {
      console.warn("[Annihilation] rest save failed", e);
    }
  }, [profile, anni.character]);

  // ---------------------------------------------------------
  // Render — one switchboard, top to bottom.
  // ---------------------------------------------------------
  if (screen === "menu") {
    return <BootSequence onComplete={() => setScreen("plaza")} />;
  }

  if (screen === "plaza") {
    return (
      <Plaza
        onEnterDungeon={handleEnterDungeon}
        onEnterShop={handleEnterShop}
        onEnterBuilding={handleEnterBuilding}
        onForge={handleForge}
        onPierre={handlePierre}
        onEnterNPC={handleEnterNPC}
        onBack={() => setScreen("menu")}
      />
    );
  }

  // ---- THE TRAINER (live room, v3) ------------------------
  // Profile unknown → placeholder (walked in logged-out).
  // Character absent → the Transcendence.
  // Character present → Sigfer's four trees.
  if (screen === "trainer" && profile?.uid) {
    if (anni.loading) {
      return (
        <VillageRoom
          building={getBuildingForScreen("trainer")}
          bloodMoney={null}
          onBack={goBack}
        />
      );
    }
    if (!anni.character) {
      return (
        <CharacterCreate
          uid={profile.uid}
          onCreated={() => {
            /* live listener swaps this screen to the trees */
          }}
          onBack={goBack}
        />
      );
    }
    return (
      <>
        <SkillTreeView
          key={profile.uid}
          uid={profile.uid}
          character={anni.character}
          bloodMoney={anni.bloodMoney}
          onBack={goBack}
        />
        {/* Batch 5: the yard door — App owns navigation, so the
            button floats here instead of teaching SkillTreeView
            about screens it shouldn't know. */}
        <button style={S.yardDoor} onClick={() => goTo("yard")}>
          ⚔ Sigfer's Yard
        </button>
      </>
    );
  }

  // ---- SIGFER'S YARD (live proving ground, v4) --------------
  if (screen === "yard" && profile?.uid && anni.character) {
    return <TrainingYard character={anni.character} onBack={goBack} />;
  }

  // ---- THE GATE (live departure point, v5) ------------------
  // The ready-up + cinematic happen in the Plaza; this room is
  // the far side of the arch — pick a trail.
  if (screen === "gate") {
    const building = getBuildingForScreen("gate");
    const hasCharacter = Boolean(profile?.uid && anni.character);
    return (
      <VillageRoom
        building={building}
        bloodMoney={profile ? anni.bloodMoney : null}
        onBack={goBack}
        extra={
          <div>
            {hasCharacter ? (
              <>
                <button style={S.trailBtn} onClick={() => goTo("grove")}>
                  🍂 The Whispering Grove — north trail
                </button>
                <button style={S.trailBtnDisabled} disabled>
                  ✦ The Glass Flats — the east trail is still being cut
                </button>
              </>
            ) : (
              <div style={S.gateWarning}>
                The Gate will not open for the unTranscended. Visit the
                Trainer — Sigfer is waiting.
              </div>
            )}
          </div>
        }
      />
    );
  }

  // ---- THE WHISPERING GROVE (live zone, v5 — Batch 6) --------
  if (screen === "grove" && profile?.uid && anni.character) {
    return (
      <WhisperingGrove
        key={`grove_${profile.uid}`}
        uid={profile.uid}
        character={anni.character}
        onBack={goBack}
        onEnterDungeon={() => goTo("sunken_fold")}
        onDown={respawnAtVillage}
      />
    );
  }

  // ---- THE SUNKEN FOLD (live dungeon, v5 — Batch 6) ----------
  if (screen === "sunken_fold" && profile?.uid && anni.character) {
    return (
      <SunkenFold
        key={`fold_${profile.uid}`}
        uid={profile.uid}
        character={anni.character}
        onBack={goBack}
        onDown={respawnAtVillage}
      />
    );
  }

  // ---- THE BONFIRE (live rest, v3) -------------------------
  if (screen === "bonfire" && profile?.uid && anni.character) {
    const building = getBuildingForScreen("bonfire");
    const { level } = levelForXp(anni.character.xp || 0);
    return (
      <VillageRoom
        building={building}
        bloodMoney={anni.bloodMoney}
        onBack={goBack}
        extra={
          <div>
            <div style={S.restLevel}>a level {level} flame keeps you</div>
            <div style={S.barHolder}>
              <SkillBar character={anni.character} />
            </div>
            <button style={S.restBtn} onClick={handleRest}>
              {rested ? "◆ rested ◆" : "Rest at the Bonfire"}
            </button>
            <div style={S.restNote}>
              Resting resets Sigfer's refund and, in time, every
              rest-gated power.
            </div>
          </div>
        }
      />
    );
  }

  // ---- THE MARKETPLACE (live shop, Blood Crests batch) ------
  // Maerie sells gathering materials for Blood Crests — a real
  // crest sink that feeds crafting at Willow's Forge (Batch 8).
  // Uses only existing write paths: deltaBloodMoney (spend) +
  // deltaMaterial (grant). Mounted through the shared VillageRoom
  // chrome via `extra`, the same pattern the Bonfire/Gate use.
  if (screen === "marketplace" && profile?.uid) {
    const building = getBuildingForScreen("marketplace");
    return (
      <VillageRoom
        building={building}
        bloodMoney={anni.bloodMoney}
        onBack={goBack}
        extra={
          anni.character ? (
            <MarketStall
              uid={profile.uid}
              crests={anni.bloodMoney}
              materials={anni.character.materials || {}}
            />
          ) : (
            <div style={S.gateWarning}>
              Maerie nods at the empty air where your name should be.
              Transcend first — the Trainer is expecting you.
            </div>
          )
        }
      />
    );
  }

  if (screen === "wilds") {
    return (
      <VillageRoom
        building={WILDS_STUB}
        bloodMoney={profile ? anni.bloodMoney : null}
        onBack={goBack}
      />
    );
  }

  const building = getBuildingForScreen(screen);
  if (building) {
    return (
      <VillageRoom
        building={building}
        bloodMoney={profile ? anni.bloodMoney : null}
        onBack={goBack}
      />
    );
  }

  // Defensive fallback — an unwired screen never shows a blank page.
  return (
    <VillageRoom
      building={{
        name: screen,
        keeper: null,
        role: "This screen isn't wired yet.",
        flavor: "A door that leads nowhere. For now.",
      }}
      bloodMoney={profile ? anni.bloodMoney : null}
      onBack={goBack}
    />
  );
}

// ============================================================
//  VillageRoom — the placeholder shell for every Old Fold
//  building until its real room ships. Reads everything from
//  villageData so the words live in exactly one place.
//  Shows the live Blood Crest balance when a profile is known.
//  `extra` lets live rooms (the Bonfire, the Gate, the
//  Marketplace) mount their verbs inside the same chrome instead
//  of forking the shell.
// ============================================================
function VillageRoom({ building, bloodMoney, onBack, extra }) {
  return (
    <div style={S.void}>
      <div style={S.frame}>
        <div style={S.kicker}>{VILLAGE.name}</div>
        <h1 style={S.title}>{building.name}</h1>
        {building.keeper ? (
          <div style={S.keeper}>kept by {building.keeper}</div>
        ) : null}
        <p style={S.role}>{building.role}</p>
        {building.flavor ? <p style={S.flavor}>{building.flavor}</p> : null}
        {typeof bloodMoney === "number" ? (
          <div style={S.bloodMoney}>🩸 {bloodMoney.toLocaleString()} Blood Crests</div>
        ) : null}
        {extra ? (
          <div style={S.extra}>{extra}</div>
        ) : (
          <div style={S.wip}>◆ this room is still being raised ◆</div>
        )}
        <button style={S.back} onClick={onBack}>
          ← back to the square
        </button>
      </div>
    </div>
  );
}

// ============================================================
//  MarketStall — Maerie's shop. Buy gathering materials with
//  Blood Crests. A genuine crest sink (crafting lands at Willow's
//  Forge, Batch 8). Spend routes through deltaBloodMoney (rejects
//  atomically if short); the material lands through deltaMaterial.
//  Spend-first, grant-after — the conservative direction.
//  Prices are dials; material defs come from whisperingGrove's
//  MATERIALS map (one source of truth — no redefinition here).
// ============================================================
const MARKET_PRICES = {
  ashen_moss: 5, //  common — cheap
  fold_iron: 15, //  uncommon — the grove only holds two nodes
};

function MarketStall({ uid, crests, materials }) {
  const [localCrests, setLocalCrests] = React.useState(null);
  const [localMats, setLocalMats] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState(null);

  const balance =
    localCrests != null ? localCrests : typeof crests === "number" ? crests : 0;

  const countOf = (id) =>
    (localMats[id] != null ? localMats[id] : materials[id] || 0);

  const buy = async (matId) => {
    if (busy) return;
    const price = MARKET_PRICES[matId] || 0;
    if (balance < price) {
      setNote("Not enough Blood Crests for that.");
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const spend = await deltaBloodMoney(uid, -price);
      if (!spend.ok) {
        setNote("Not enough Blood Crests for that.");
        setBusy(false);
        return;
      }
      setLocalCrests(
        typeof spend.balance === "number" ? spend.balance : balance - price
      );
      const got = await deltaMaterial(uid, matId, 1);
      const newCount =
        got && typeof got.count === "number" ? got.count : countOf(matId) + 1;
      setLocalMats((m) => ({ ...m, [matId]: newCount }));
      setNote(`Bought 1 ${MATERIALS[matId]?.name || matId}.`);
    } catch (e) {
      console.warn("[annihilation] market buy failed", e);
      setNote("The stall didn't take — try again.");
    } finally {
      setBusy(false);
    }
  };

  const ids = Object.keys(MARKET_PRICES);

  return (
    <div style={S.market}>
      <div style={S.marketHead}>
        <span style={S.marketTitle}>Maerie's Stall</span>
        <span style={S.marketCrests}>🩸 {balance.toLocaleString()} Blood Crests</span>
      </div>
      {ids.map((id) => {
        const mat = MATERIALS[id] || { name: id };
        const price = MARKET_PRICES[id];
        const affordable = balance >= price;
        return (
          <div key={id} style={S.marketRow}>
            <div style={S.marketItem}>
              <div style={S.marketName}>{mat.name}</div>
              <div style={S.marketHave}>you hold {countOf(id)}</div>
            </div>
            <button
              style={{ ...S.marketBuy, ...(affordable && !busy ? null : S.marketBuyOff) }}
              disabled={!affordable || busy}
              onClick={() => buy(id)}
            >
              Buy · {price} 🩸
            </button>
          </div>
        );
      })}
      {note ? <div style={S.marketNote}>{note}</div> : null}
      <div style={S.marketFoot}>
        Materials feed crafting at Willow's Forge — coming soon.
      </div>
    </div>
  );
}

const S = {
  void: {
    minHeight: "100vh",
    background:
      "radial-gradient(ellipse at 50% 30%, #14101c 0%, #0a0810 55%, #050408 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    boxSizing: "border-box",
  },
  frame: {
    maxWidth: 520,
    width: "100%",
    border: "1px solid rgba(232,163,61,0.35)",
    borderRadius: 12,
    padding: "36px 32px",
    background: "rgba(12,10,18,0.85)",
    boxShadow: "0 0 60px rgba(232,163,61,0.08), inset 0 0 40px rgba(0,0,0,0.5)",
    textAlign: "center",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  kicker: {
    color: "#8a7a5c",
    fontSize: 12,
    letterSpacing: "0.35em",
    textTransform: "uppercase",
    marginBottom: 14,
  },
  title: {
    color: "#e8a33d",
    fontSize: 30,
    margin: "0 0 8px",
    fontWeight: 600,
    textShadow: "0 0 24px rgba(232,163,61,0.35)",
  },
  keeper: {
    color: "#b5a284",
    fontSize: 14,
    fontStyle: "italic",
    marginBottom: 18,
  },
  role: {
    color: "#d8cdb8",
    fontSize: 15,
    lineHeight: 1.6,
    margin: "0 0 14px",
  },
  flavor: {
    color: "#9a8d74",
    fontSize: 13.5,
    lineHeight: 1.6,
    fontStyle: "italic",
    margin: "0 0 26px",
  },
  wip: {
    color: "#6d5f47",
    fontSize: 11,
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    marginBottom: 26,
  },
  extra: { marginBottom: 26 },
  bloodMoney: {
    display: "inline-block",
    color: "#e86a5c",
    fontSize: 13,
    letterSpacing: "0.08em",
    border: "1px solid rgba(232,106,92,0.4)",
    borderRadius: 999,
    padding: "5px 16px",
    marginBottom: 22,
    background: "rgba(232,106,92,0.07)",
  },
  // ---- Maerie's Stall (Marketplace shop) -------------------
  market: {
    textAlign: "left",
    border: "1px solid rgba(232,106,92,0.3)",
    borderRadius: 10,
    padding: "14px 16px",
    background: "rgba(232,106,92,0.05)",
  },
  marketHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 12,
  },
  marketTitle: {
    color: "#e86a5c",
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: "0.05em",
  },
  marketCrests: { color: "#e86a5c", fontSize: 12.5 },
  marketRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    padding: "8px 0",
    borderTop: "1px solid rgba(232,106,92,0.12)",
  },
  marketItem: { display: "flex", flexDirection: "column" },
  marketName: { color: "#d8cdb8", fontSize: 14 },
  marketHave: { color: "#8a7a5c", fontSize: 11, fontStyle: "italic" },
  marketBuy: {
    background: "rgba(232,106,92,0.12)",
    border: "1px solid rgba(232,106,92,0.6)",
    color: "#e86a5c",
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 12.5,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  marketBuyOff: {
    opacity: 0.4,
    cursor: "default",
    borderColor: "rgba(109,95,71,0.4)",
    color: "#6d5f47",
    background: "rgba(109,95,71,0.06)",
  },
  marketNote: {
    color: "#e86a5c",
    fontSize: 11.5,
    fontStyle: "italic",
    marginTop: 10,
  },
  marketFoot: {
    color: "#6d5f47",
    fontSize: 10.5,
    fontStyle: "italic",
    marginTop: 12,
    textAlign: "center",
  },
  back: {
    background: "transparent",
    border: "1px solid rgba(232,163,61,0.5)",
    color: "#e8a33d",
    padding: "10px 22px",
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.05em",
  },
  // Bonfire rest extras
  restLevel: {
    color: "#9a8d74",
    fontSize: 12,
    fontStyle: "italic",
    marginBottom: 12,
  },
  barHolder: { marginBottom: 16 },
  restBtn: {
    background: "rgba(232,163,61,0.12)",
    border: "1px solid rgba(232,163,61,0.7)",
    color: "#e8a33d",
    padding: "11px 24px",
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.05em",
    marginBottom: 10,
  },
  restNote: { color: "#6d5f47", fontSize: 11, fontStyle: "italic" },
  yardDoor: {
    position: "fixed",
    top: 14,
    right: 14,
    zIndex: 150,
    background: "rgba(12,10,18,0.9)",
    border: "1px solid rgba(232,163,61,0.6)",
    color: "#e8a33d",
    padding: "9px 16px",
    borderRadius: 999,
    fontSize: 12.5,
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    letterSpacing: "0.06em",
    boxShadow: "0 2px 14px rgba(0,0,0,0.6)",
  },
  // Gate departure extras (v5)
  trailBtn: {
    display: "block",
    width: "100%",
    background: "rgba(143,209,138,0.08)",
    border: "1px solid rgba(143,209,138,0.55)",
    color: "#8fd18a",
    padding: "12px 18px",
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.04em",
    marginBottom: 10,
  },
  trailBtnDisabled: {
    display: "block",
    width: "100%",
    background: "rgba(109,95,71,0.06)",
    border: "1px solid rgba(109,95,71,0.4)",
    color: "#6d5f47",
    padding: "12px 18px",
    borderRadius: 8,
    fontSize: 13,
    fontFamily: "inherit",
    letterSpacing: "0.04em",
    cursor: "default",
  },
  gateWarning: {
    color: "#b5a284",
    fontSize: 13.5,
    fontStyle: "italic",
    lineHeight: 1.6,
  },
};
